import ProductName from '@web/components/ProductName'
import { Link } from 'react-router-dom'
import { useEffect } from 'react'

const PAGE_TITLE =
  'Resilience Explorer® | Property methodology · NPS-NH risk classification, consequence modelling, scope · Urban Intelligence'
const PAGE_DESCRIPTION =
  'How Resilience Explorer® | Property calculates natural hazard risk. NPS-NH risk classifications, likelihood and consequence framework, vulnerability functions, and scope and limitations.'

const riskCategories = [
  {
    level: 'Very High',
    body: 'Development that would result in Very High natural hazard risk must be avoided (Policy 3; Clause 3.3.2(a)).',
  },
  {
    level: 'High',
    body: 'Risk must be avoided or mitigated in a manner proportionate to its level (Clause 3.3(2)(b)).',
  },
  {
    level: 'Medium',
    body: 'Risk must be managed through mitigation measures proportionate to its level (Clause 3.3(2)(b)).',
  },
  {
    level: 'Low',
    body: 'Risk management response must be proportionate to the assessed level of risk (Clause 3.3).',
  },
]

export function PropertyMethodologyPage() {
  useEffect(() => {
    const previousTitle = document.title
    const meta = document.querySelector('meta[name="description"]')
    const previousDescription = meta?.getAttribute('content') ?? null

    document.title = PAGE_TITLE
    meta?.setAttribute('content', PAGE_DESCRIPTION)

    return () => {
      document.title = previousTitle
      if (previousDescription !== null) {
        meta?.setAttribute('content', previousDescription)
      }
    }
  }, [])

  return (
    <article className="bg-white">
      <div className="mx-auto max-w-3xl px-6 lg:px-8 py-24 sm:py-16 space-y-12">
        <header className="space-y-4">
          <Link
            to="/products/property"
            className="text-sm font-semibold text-brand-blue no-underline hover:underline"
          >
            ← <ProductName />
          </Link>
          <span className="block text-xs font-semibold uppercase tracking-widest text-brand-blue">
            Methodology
          </span>
          <h1 className="text-4xl font-light text-brand-navy m-0">
            How <ProductName /> calculates natural hazard risk.
          </h1>
          <p className="text-base text-brand-navy/80 leading-relaxed m-0">
            This page sets out the risk-classification framework <ProductName /> applies, the
            consequence modelling behind every property-level rating, and the scope and limitations
            of every report. It is intended for GIS managers, natural hazards advisors, and senior
            consents planners.
          </p>
        </header>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-brand-navy m-0">
            Understanding NPS-NH risk classifications
          </h2>
          <p className="text-base text-brand-navy leading-relaxed">
            The National Policy Statement for Natural Hazards 2025 (NPS-NH) is national direction
            issued under the Resource Management Act 1991. It requires councils to assess natural
            hazard risk using a standardised framework when considering subdivision, use, and
            development.
          </p>
          <p className="text-base text-brand-navy leading-relaxed">
            Under the NPS-NH, risk is determined by combining <strong>likelihood</strong> (how often
            a hazard event may occur) and <strong>consequence</strong> (potential impacts on
            property and life safety). These are combined using the risk matrix in Appendix 1 of the
            NPS-NH to classify risk as Low, Medium, High, or Very High. Where property and
            life-safety consequences differ, <ProductName /> adopts the higher consequence level in
            determining overall risk.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-brand-navy m-0">Risk categories</h2>
          <dl className="space-y-3 bg-neutral-muted rounded-lg p-6">
            {riskCategories.map((cat) => (
              <div key={cat.level} className="space-y-1">
                <dt className="text-sm font-bold uppercase tracking-wider text-brand-blue m-0">
                  {cat.level} risk
                </dt>
                <dd className="text-base text-brand-navy leading-relaxed m-0">{cat.body}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-brand-navy m-0">Timeframe</h2>
          <p className="text-base text-brand-navy leading-relaxed">
            The NPS-NH requires consideration of climate change impacts for at least 100 years
            (Policy 6). Where risk increases over time, this may influence consent decisions,
            particularly for long-lived assets. <ProductName /> runs both a present-day and a
            long-term (100-year) assessment for every property, using the climate scenarios agreed
            with your council.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-brand-navy m-0">Consequence modelling</h2>
          <p className="text-base text-brand-navy leading-relaxed">
            Property damage and life-safety consequences are derived using hazard-specific
            consequence models embedded within Resilience Explorer®. For property damage,
            vulnerability (damage ratio) functions are used to estimate the expected level of
            building damage based on hazard intensity (for example, flood depth or liquefaction
            category). These functions represent typical damage responses and do not account for
            site-specific building design, floor levels, or construction details.
          </p>
          <p className="text-base text-brand-navy leading-relaxed">
            For life safety, hazard-specific thresholds are applied to classify potential
            life-safety consequences based on hazard intensity. These classifications indicate the
            level of potential harm under the modelled conditions and do not represent predicted
            fatality counts.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-brand-navy m-0">
            Consequence functions and their basis
          </h2>
          <p className="text-base text-brand-navy leading-relaxed">
            <ProductName />
            &apos;s consequence calculations use vulnerability (damage ratio) functions derived from
            a combination of published academic literature, post-event damage studies from New
            Zealand events including the Canterbury earthquake sequence and the 2023 North Island
            weather events, and Urban Intelligence&apos;s own peer-reviewed research. The functions
            are calibrated for New Zealand building stock and construction typologies. They are
            reviewed annually and updated when new evidence becomes available.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-brand-navy m-0">Scope and limitations</h2>
          <p className="text-base text-brand-navy leading-relaxed">
            <ProductName /> assessments provide a screening-level classification of natural hazard
            risk at the subject site, based on regional hazard datasets and modelling available at
            the time of report generation. Results may not reflect site-specific ground conditions,
            building design, floor levels, or proposed mitigation measures.
          </p>
          <p className="text-base text-brand-navy leading-relaxed m-0">
            <ProductName /> does not evaluate:
          </p>
          <ul className="text-base text-brand-navy leading-relaxed list-disc pl-6 space-y-1">
            <li>The effects of any specific subdivision, use, or development proposal</li>
            <li>The effectiveness or cost-effectiveness of mitigation measures</li>
            <li>Residual risk following mitigation</li>
            <li>Whether development may create or increase natural hazard risk on other sites</li>
          </ul>
          <p className="text-base text-brand-navy leading-relaxed">
            These matters must be considered separately as part of a proposal-specific consent
            assessment. Properties classified as Medium, High, or Very High risk may require
            detailed specialist investigation to support consent applications. Risk classifications
            follow the NPS-NH Appendix 1 risk matrix as prescribed.
          </p>
        </section>

        <footer className="pt-8 border-0 border-t border-solid border-neutral-charcoal/15 space-y-3">
          <p className="text-sm text-brand-navy/80 leading-relaxed m-0">
            Reports generated by <ProductName /> provide a screening-level assessment of natural
            hazard risk based on regional hazard datasets available at the time of generation. They
            support preliminary assessment and do not replace site-specific investigations, detailed
            engineering analysis, or professional advice tailored to a specific development
            proposal.
          </p>
          <p className="text-sm text-brand-navy/60 leading-relaxed m-0">
            Property is a module of Resilience Explorer®, built by Urban Intelligence Ltd, Ōtautahi
            Christchurch.
          </p>
          <p className="m-0 pt-2">
            <Link
              to="/products/property"
              className="text-sm font-semibold text-brand-blue no-underline hover:underline"
            >
              ← Back to <ProductName />
            </Link>
          </p>
        </footer>
      </div>
    </article>
  )
}
